/**
 * SpecialMessageTile Component
 *
 * A reusable message card component for displaying special messages with:
 * - Configurable message types (info, success, warning, error, research, etc.)
 * - Action buttons with various action types (navigate, open_url, dismiss, etc.)
 * - Optional icon, title, subtitle, and content
 * - Streaming/loading state support
 * - Attachments display
 *
 * Used in agentic workflows (deep research, formatting) and platform notifications.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  SpecialTileContainer,
  SpecialTileHeader,
  SpecialTileIcon,
  SpecialTileTitle,
  SpecialTileSubtitle,
  SpecialTileContent,
  SpecialTileActions,
  SpecialTileActionButton,
  SpecialTileTimestamp,
  SpecialTileStreamingIndicator,
  SpecialTileFooter,
  SpecialTileProcessingTime,
  SPECIAL_TILE_COLORS,
  CopyIconButton,
  ReadAloudButton,
  TokenInfoButton,
  TokenInfoPopup,
  TokenInfoHeader,
  TokenInfoCostSection,
  TokenInfoCostTotal,
  TokenInfoCostTotalLabel,
  TokenInfoCostTotalValue,
} from './styles';
import { SpeakerSvgIcon, StopSvgIcon } from './components';
import { ReadAloudMiniPlayerLive } from './ReadAloudMiniPlayer';
import { AttachmentsSection } from './ChatAttachment';
import { threeDArtifactIdFromRoute } from '../FilePreview/fileLinks';
import { autoLinkText, ensureLinksOpenInNewTab, normalizeChatHtml, formatMessageTime, formatProcessingTime, htmlToPlainText } from './utils';
// L4 (2026-07-15): guest usage privacy — signed-out viewers never see
// token-usage/cost details; the ⓘ popup body becomes a generic sign-in notice.
import { useViewerIsSignedOut, GuestUsageNotice } from './guestUsagePrivacy';
import { OrgUsageNotice, useUsageDetailRestricted } from './orgUsagePrivacy';
import { auxSidecarStepLabel, costEstimateMarker, COST_ESTIMATE_TITLE } from '../../config/usageVocabulary';
import { rollUpSidecarSteps, rollupPartsLabel, usageShowsCompute } from './tokenUsageRollup';
import type {
  SpecialMessageTileProps,
  SpecialMessageActionButton,
  SpecialMessageType,
  TokenUsageMetadata,
  TokenUsageSnapshot,
} from './types';
import { calculateCost, hasKnownPricing } from '../../config/aiModelPricing';
// Single source of truth for the user-facing system identity (vendor-name-safe).
// 2026-08-13 WU migration: the popup renders the versionless STATION name via
// getPublicSystemNameForUsage — versioned codenames (getModelCodeName*) are
// internal-only and must never render in UI again. Always import the
// canonical resolver (this file once kept a stale local copy that drifted).
import { getPublicSystemNameForUsage, getProviderColor, getSystemDotColor, resolveEffortCodeWordForDisplay } from '../../config/providerCodeNames';
import { InlineInvoiceMessage } from './InlineInvoiceMessage';
import { InlineAppointmentMessage } from './InlineAppointmentMessage';
import {
  isWorkflowCtaButtonId,
  isWorkflowCtaExemptId,
  applyButtonSelectionState,
  retireAllButtonsHtml,
  retireWorkflowCtaButtonsHtml,
  normalizeLegacyCtaColors,
} from './workflowCtaLifecycle';
import { OlvCostCell } from './OlvCostCell';

// ============================================================================
// Helper: Apply selection state to workflow action buttons HTML
// ============================================================================

// applyButtonSelectionState moved to ./workflowCtaLifecycle (2026-08-27) —
// a pure html transform that belongs with its siblings (retireAllButtonsHtml,
// normalizeLegacyCtaColors); re-imported above. Its call sites here are
// pinned by ctaButtonPalette.test.ts and workflowCtaLifecycle.test.ts.

// ============================================================================
// Internal Model Code Names & Provider Colors (shared with GlobalChatSystem)
// ============================================================================

// MODEL_CODE_NAMES + getModelCodeName now imported from ../../config/providerCodeNames
// (canonical, vendor-name-safe). Do NOT reintroduce a local copy here — it drifts.
// PROVIDER_COLORS + getProviderColor likewise moved to the leaf config module
// (2026-07-06): the local copy here had drifted to a 4-provider subset, so
// fleet turns served by Gemini/Grok/GLM/etc rendered the 'unknown' slate dot
// in tool tiles. (Imported in the top import block — see line ~63.)

// Summary info icon with hover tooltip and click-to-copy (matches wallet page design)
const SummaryInfoIcon: React.FC<{ summary: string }> = ({ summary }) => {
  const [copied, setCopied] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    navigator.clipboard.writeText(summary).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => {});
  };

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: '6px',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); }}
    >
      {/* Clickable icon button */}
      <span
        onClick={handleCopy}
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '22px',
          height: '22px',
          borderRadius: '6px',
          background: copied
            ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
            : isHovered
              ? 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)'
              : '#f1f5f9',
          border: `1px solid ${copied ? '#86efac' : 'rgba(0,0,0,0.06)'}`,
          color: copied ? '#16a34a' : '#64748b',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          transform: isHovered && !copied ? 'scale(1.08)' : 'scale(1)',
          boxShadow: isHovered && !copied ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
        }}
      >
        {copied ? (
          // Checkmark icon when copied
          <svg viewBox="0 0 24 24" style={{
            width: '13px',
            height: '13px',
            stroke: 'currentColor',
            strokeWidth: 2.5,
            fill: 'none',
          }}>
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          // Info icon (default)
          <svg viewBox="0 0 24 24" style={{
            width: '13px',
            height: '13px',
            stroke: 'currentColor',
            strokeWidth: 2.5,
            fill: 'none',
          }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        )}
      </span>

      {/* Hover tooltip popup showing summary */}
      {(isHovered || copied) && (
        <span
          onClick={handleCopy}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: copied
              ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
              : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: '8px',
            fontSize: '0.75rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            boxShadow: copied
              ? '0 4px 16px rgba(22, 163, 74, 0.3)'
              : '0 4px 20px rgba(0, 0, 0, 0.25)',
            zIndex: 100,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {copied ? (
            <>
              <svg viewBox="0 0 24 24" style={{
                width: '12px',
                height: '12px',
                stroke: 'currentColor',
                strokeWidth: 2.5,
                fill: 'none',
              }}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>copied!</span>
            </>
          ) : (
            <>
              <span>{summary}</span>
              <span style={{
                padding: '2px 6px',
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '4px',
                fontSize: '0.65rem',
              }}>
                click to copy
              </span>
            </>
          )}
          {/* Tooltip arrow */}
          <span
            style={{
              position: 'absolute',
              bottom: '-6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: copied ? '6px solid #15803d' : '6px solid #0f172a',
            }}
          />
        </span>
      )}
    </span>
  );
};

// ============================================================================
// Token Info Popup for SpecialMessageTile
// ============================================================================

interface SpecialTileTokenInfoPopupProps {
  tokenUsage?: TokenUsageMetadata | TokenUsageSnapshot | null;
  tokenUsages?: (TokenUsageMetadata | TokenUsageSnapshot)[];
  anchorRef: React.RefObject<HTMLElement>;
  onClose: () => void;
}

const SpecialTileTokenInfoPopup: React.FC<SpecialTileTokenInfoPopupProps> = ({
  tokenUsage,
  tokenUsages,
  anchorRef,
  onClose,
}) => {
  const popupRef = React.useRef<HTMLDivElement>(null);

  // L4 (2026-07-15): view-time usage-privacy gate — signed-out viewers never
  // see token counts / model identities / cost figures (generic notice below).
  const viewerSignedOut = useViewerIsSignedOut();
  // ORG-USAGE-2026: kept in LOCKSTEP with TokenInfoPopupContent in
  // GlobalChatSystem.tsx — these two popups have drifted before.
  const orgUsageRestricted = useUsageDetailRestricted();

  // Build array of usages from either array prop or single prop (backward compatible)
  const allUsages: TokenUsageSnapshot[] = React.useMemo(() => {
    if (tokenUsages && tokenUsages.length > 0) {
      // ONE LINE THAT GROWS — the same collapse the host popup makes, from the
      // same module, because these two have drifted before (2026-09-21).
      return rollUpSidecarSteps(tokenUsages.map((u) => (u as TokenUsageSnapshot) || { provider: 'unknown' })) as TokenUsageSnapshot[];
    }
    if (tokenUsage) {
      return [(tokenUsage as TokenUsageSnapshot) || { provider: 'unknown' }];
    }
    return [{ provider: 'unknown' } as TokenUsageSnapshot];
  }, [tokenUsage, tokenUsages]);

  const usage: TokenUsageSnapshot = allUsages[0];
  const isMultiModel = allUsages.length > 1;

  const [style, setStyle] = React.useState<React.CSSProperties>({ opacity: 0 });

  // R15 unit-lockstep (2026-08-09): kept in LOCKSTEP with
  // TokenInfoPopupContent in GlobalChatSystem.tsx via the shared OlvCostCell
  // — one popup-level unit toggle; hovering any WU cell flips every cell
  // to USD together so the breakdown never shows mixed WU/USD units.
  // 2026-08-13 WU migration: the cell renders PRO Work Units derived from the
  // same user-cost USD at the fixed Standard Rate (src/config/workUnits.ts).
  const [showUsd, setShowUsd] = React.useState(false);
  const renderWuCost = (usdCost: number | undefined) => (
    <OlvCostCell usdCost={usdCost} showUsd={showUsd} onHoverChange={setShowUsd} />
  );

  // ONE rule, shared with TokenInfoPopupContent (tokenUsageRollup.ts). It keeps
  // this file's own reading — every entry, not just the first, which a sparse
  // Stage-1 deep-research row needs — and adds the sidecar half: a keyed,
  // separately-billed step means somebody paid, whoever wrote the message
  // (2026-09-21). This tile renders assistant work, so `isUser` is false here;
  // passing it explicitly is what keeps the two popups one rule and not two.
  const requiredAiProcessing = usageShowsCompute(allUsages, false);

  // Position the popup
  React.useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const popup = popupRef.current;
    if (!anchor || !popup) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();
    const gap = 12;
    const viewportPadding = 12;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Position above the button
    let top = anchorRect.top - gap - popupRect.height;
    if (top < viewportPadding) {
      top = anchorRect.bottom + gap; // Position below if not enough space above
    }
    top = Math.max(viewportPadding, Math.min(top, viewportHeight - popupRect.height - viewportPadding));

    let left = anchorRect.right - popupRect.width;
    left = Math.max(viewportPadding, Math.min(left, viewportWidth - popupRect.width - viewportPadding));

    setStyle({ top, left, opacity: 1 });
  }, [anchorRef, tokenUsage]);

  // Close on click outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const formatResponseTime = (ms?: number): string => {
    if (!ms) return 'n/a';
    if (ms < 1000) return `${ms}ms`;
    const totalSeconds = Math.floor(ms / 1000);
    if (totalSeconds < 60) return ms < 10000 ? `${(ms / 1000).toFixed(1)}s` : `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes < 60) return `${minutes}m ${seconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m ${seconds}s`;
  };

  // Calculate combined totals for multi-model display
  const combinedTotalCost = React.useMemo(() => {
    if (!isMultiModel) return 0;
    return allUsages.reduce((sum, u) => {
      const cost = (u as TokenUsageSnapshot).cost || calculateCost(u);
      return sum + (cost?.totalCost || 0);
    }, 0);
  }, [allUsages, isMultiModel]);

  // Render a single system-call section. 2026-08-13 WU migration (LOCKSTEP
  // with TokenInfoPopupContent): the title is the versionless STATION name
  // (getPublicSystemNameForUsage) — never a versioned codename or raw token
  // counts; the only figure shown per call is its PRO Work Units.
  const renderModelSection = (u: TokenUsageSnapshot, index: number, isCompact: boolean) => {
    // 2026-08-15: color by the MODEL's family (true vendor) — see the
    // GlobalChatSystem popup note; the two popups stay in lockstep.
    const uProviderColor = getSystemDotColor(u.model, u.provider);
    const uStationName = getPublicSystemNameForUsage(u.model, u.provider);
    // The SAME rule again, one step down: does THIS row represent work that was
    // paid for? Read from tokenUsageRollup so the popup and its own sections
    // cannot disagree — its twin in GlobalChatSystem carried `!isUser`
    // here too, and this one did not (2026-09-21).
    const uRequiredAiProcessing = usageShowsCompute([u], false);
    const uCostBreakdown = (u as TokenUsageSnapshot).cost || calculateCost(u);

    return (
      <div key={index} style={{ marginBottom: isCompact && index < allUsages.length - 1 ? '12px' : 0 }}>
        {isCompact && index > 0 && (
          <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', marginBottom: isCompact ? '8px' : '12px' }} />
        )}

        {uRequiredAiProcessing && (
          <TokenInfoHeader $compact={isCompact}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isCompact ? '6px' : '8px' }}>
              <span
                style={{
                  width: isCompact ? '8px' : '10px',
                  height: isCompact ? '8px' : '10px',
                  borderRadius: '50%',
                  background: uProviderColor,
                  flexShrink: 0,
                }}
                title="AI Provider"
              />
              <span
                style={{
                  fontSize: isCompact ? '0.72rem' : '0.8rem',
                  fontWeight: 600,
                  color: uProviderColor,
                  letterSpacing: '0.05em',
                }}
              >
                {uStationName}
                {/* 2026-08-16 (Miles): the CODED effort designator, LOCKSTEP
                    with TokenInfoPopupContent — always displayed: stored
                    effort wins, else the step's model resolves it from the
                    SSOT (resolveEffortCodeWordForDisplay); never the raw
                    vendor enum level. */}
                {resolveEffortCodeWordForDisplay(u.model, u.effort) && (
                  <span style={{ opacity: 0.55, fontWeight: 400 }}>
                    {` · ${resolveEffortCodeWordForDisplay(u.model, u.effort)}`}
                  </span>
                )}
              </span>
              {/* R6: per-call ordinal (LOCKSTEP with TokenInfoPopupContent). */}
              {isCompact && (
                <span
                  style={{
                    fontSize: '0.62rem',
                    fontWeight: 500,
                    color: '#94a3b8',
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  step {index + 1}
                </span>
              )}
              {/* 2026-08-16 (Miles): background-step label (LOCKSTEP with
                  TokenInfoPopupContent) — memory update / follow-up
                  suggestions sidecars read as part of the task. */}
              {auxSidecarStepLabel(u as { billedSeparately?: boolean; sidecarKey?: string; stage?: string }) && (
                <span
                  style={{
                    fontSize: '0.62rem',
                    fontWeight: 500,
                    color: '#b8860b',
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {auxSidecarStepLabel(u as { billedSeparately?: boolean; sidecarKey?: string; stage?: string })}
                  {/* …and how many of them this one line stands for. A readout
                      of a long message is part after part, each its own render
                      and its own charge; the ⓘ says so without a column of
                      near-identical rows (2026-09-21, Miles's own choice). */}
                  {rollupPartsLabel(u) && (
                    <span style={{ opacity: 0.75, fontWeight: 400 }}>{` · ${rollupPartsLabel(u)}`}</span>
                  )}
                </span>
              )}
              {/* Info icon with summary hover tooltip - click to copy */}
              {u.summary && (
                <SummaryInfoIcon summary={u.summary} />
              )}
            </div>
          </TokenInfoHeader>
        )}

        {u.responseTimeMs && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isCompact ? '4px' : '6px',
              padding: isCompact ? '1px 0 4px 0' : '2px 0 10px 0',
              fontSize: isCompact ? '0.7rem' : '0.76rem',
              color: '#64748b',
              fontWeight: 500,
            }}
            title="compute time"
          >
            <svg
              viewBox="0 0 24 24"
              style={{
                width: isCompact ? '12px' : '14px',
                height: isCompact ? '12px' : '14px',
                stroke: 'currentColor',
                strokeWidth: 2,
                fill: 'none',
                opacity: 0.7,
              }}
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatResponseTime(u.responseTimeMs)}</span>
          </div>
        )}

        {/* ONE line per call: the call's PRO Work Units (2026-08-13 mandate —
            no token grid, no per-component cost rows; LOCKSTEP with
            TokenInfoPopupContent). The WU derives from the SAME per-call
            user-cost USD the popup computed before (persisted cost snapshot
            or calculateCost/upstreamCostUsd), and is also shown when the
            usage carries an authoritative upstream cost (or the
            unknown-model floor produced a nonzero estimate) — the wallet
            debits these turns, so the popup must not hide the figure just
            because the model has no pricing-table row. */}
        {uCostBreakdown && (hasKnownPricing(u.model) || (u as TokenUsageSnapshot).cost || (uCostBreakdown.totalCost ?? 0) > 0) && (
          <TokenInfoCostSection $compact={isCompact}>
            <TokenInfoCostTotal $compact={isCompact}>
              <TokenInfoCostTotalLabel $compact={isCompact}>work units</TokenInfoCostTotalLabel>
              <TokenInfoCostTotalValue $compact={isCompact}>
                {/* 2026-09-08: a unit-priced media render is an ESTIMATE — say so (lockstep with GlobalChatSystem). */}
                {costEstimateMarker(u) && (
                  <span data-cost-estimate="" title={COST_ESTIMATE_TITLE} aria-label="estimated" style={{ marginRight: '0.15em' }}>
                    {costEstimateMarker(u)}
                  </span>
                )}
                {renderWuCost(uCostBreakdown.totalCost)}
              </TokenInfoCostTotalValue>
            </TokenInfoCostTotal>
          </TokenInfoCostSection>
        )}
      </div>
    );
  };

  const content = (
    <TokenInfoPopup
      ref={popupRef}
      $position="above"
      style={style}
      data-prest0n-portal="true"
      onClick={(e) => e.stopPropagation()}
    >
      {/* L4: signed-out viewers get ONE uniform generic message — never token
          counts, model identities, cost figures, or the no-compute fallback. */}
      {viewerSignedOut ? (
        <GuestUsageNotice />
      ) : orgUsageRestricted ? (
        <OrgUsageNotice />
      ) : (
        <>
          {!requiredAiProcessing && (
            <div
              style={{
                fontSize: '0.76rem',
                color: '#94a3b8',
                padding: '10px 12px',
                lineHeight: 1.45,
                background: 'rgba(0, 0, 0, 0.02)',
                borderRadius: '8px',
                border: '1px solid rgba(0, 0, 0, 0.03)',
              }}
            >
              no extensive compute was necessary for this message
            </div>
          )}

          {requiredAiProcessing && (
            <>
              {/* R6: multi-call turns announce the list — same contract as
                  TokenInfoPopupContent (LOCKSTEP). */}
              {isMultiModel && (
                <div
                  style={{
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: '#94a3b8',
                    letterSpacing: '0.06em',
                    paddingBottom: '8px',
                    marginBottom: '10px',
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    position: 'sticky',
                    top: '-18px',
                    background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
                    zIndex: 1,
                  }}
                >
                  {allUsages.length} steps · in step order
                </div>
              )}
              {allUsages.map((u, i) => renderModelSection(u, i, isMultiModel))}

              {/* Turn-total WU for multi-call turns (LOCKSTEP) */}
              {isMultiModel && combinedTotalCost > 0 && (
                <>
                  <div style={{ borderTop: '2px solid rgba(0,0,0,0.1)', margin: '12px 0' }} />
                  <TokenInfoCostTotal>
                    <TokenInfoCostTotalLabel>combined total</TokenInfoCostTotalLabel>
                    <TokenInfoCostTotalValue>{renderWuCost(combinedTotalCost)}</TokenInfoCostTotalValue>
                  </TokenInfoCostTotal>
                </>
              )}
            </>
          )}
        </>
      )}
    </TokenInfoPopup>
  );

  return ReactDOM.createPortal(content, document.body);
};

// ============================================================================
// Default Icons for Message Types
// ============================================================================

const MessageTypeIcons: Record<SpecialMessageType, React.ReactNode> = {
  notification: (
    <svg viewBox="0 0 24 24">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 24 24">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  ),
  success: (
    <svg viewBox="0 0 24 24">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  system: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  research: (
    <svg viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="11" y1="8" x2="11" y2="14" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  ),
  formatting: (
    <svg viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  progress: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  payment: (
    <svg viewBox="0 0 24 24">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  appointment: (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
};

// ============================================================================
// SpecialMessageTile Component
// ============================================================================

export const SpecialMessageTile: React.FC<SpecialMessageTileProps> = ({
  text,
  html,
  messageId,
  messageType = 'info',
  priority = 'normal',
  title,
  subtitle,
  icon,
  showIcon = true,
  actionButtons = [],
  timestamp,
  showTimestamp = false,
  expiresAt,
  onDismiss,
  onAction,
  isStreaming = false,
  className,
  animate = true,
  attachments,
  attachmentsSerialized,
  tokenUsage,
  tokenUsageSnapshot,
  tokenUsages,
  tokenUsageSnapshots,
  workflowActionButtonsHtml,
  sourceTag,
  onCopyMessage,
  readAloud,
  tags,
  selectedActionButtonIndex,
  ctaLive = true,
  suppressAttribution = false,
}) => {
  const navigate = useNavigate();
  // Retired CTA (2026-09-02 owner directive): messages persisted before this
  // date carry the full-width "Open interactive 3D preview" navigate button.
  // The standard attachment card is the one convention now — its Preview
  // opens the interactive scene, Export / Download saves it — so legacy 3D
  // navigate buttons are filtered out here and the card stands alone.
  const visibleActionButtons = React.useMemo(
    () => actionButtons.filter(
      (b) => !(b.actionType === 'navigate' && threeDArtifactIdFromRoute(b.actionValue)),
    ),
    [actionButtons],
  );
  const [dismissed, setDismissed] = React.useState(false);
  const [loadingButtons, setLoadingButtons] = React.useState<Set<number>>(new Set());
  const [showTokenInfo, setShowTokenInfo] = React.useState(false);
  const tokenInfoButtonRef = React.useRef<HTMLButtonElement>(null);

  // Auto-dismiss on expiration
  React.useEffect(() => {
    if (!expiresAt) return;

    const checkExpiration = () => {
      if (new Date() >= expiresAt) {
        setDismissed(true);
        onDismiss?.();
      }
    };

    // Check immediately
    checkExpiration();

    // Then check periodically
    const interval = setInterval(checkExpiration, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, onDismiss]);

  // Handle action button click
  const handleAction = async (button: SpecialMessageActionButton, index: number) => {
    // Notify parent of action
    onAction?.(button);

    // Set loading state
    setLoadingButtons(prev => new Set(prev).add(index));

    try {
      switch (button.actionType) {
        case 'navigate': {
          navigate(button.actionValue);
          break;
        }

        case 'open_url':
          window.open(button.actionValue, '_blank', 'noopener,noreferrer');
          break;

        case 'deep_link':
          window.location.href = button.actionValue;
          break;

        case 'dismiss':
          setDismissed(true);
          onDismiss?.();
          break;

        case 'copy_text':
          await navigator.clipboard.writeText(button.actionValue);
          break;

        case 'download':
          // Trigger download
          const link = document.createElement('a');
          link.href = button.actionValue;
          link.download = button.actionValue.split('/').pop() || 'download';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          break;

        case 'callback':
        case 'custom':
          if (button.onClick) {
            await button.onClick();
          }
          break;

        case 'confirm':
          const confirmed = window.confirm('Are you sure?');
          if (confirmed && button.onClick) {
            await button.onClick();
          }
          break;

        case 'form':
          // Form handling is typically done by parent
          if (button.onClick) {
            await button.onClick();
          }
          break;

        default:
          console.warn(`Unknown action type: ${button.actionType}`);
      }
    } catch (error) {
      console.error('Action failed:', error);
    } finally {
      setLoadingButtons(prev => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }
  };

  // Don't render if dismissed
  if (dismissed) {
    return null;
  }

  // Render content
  const renderContent = () => {
    // Special handling for payment messages - render InlineInvoiceMessage component
    if (messageType === 'payment' && html?.includes('inline-payment-form')) {
      const configMatch = html.match(/data-payment-config='([^']+)'/);
      if (configMatch) {
        try {
          const configJson = configMatch[1].replace(/&#39;/g, "'");
          const config = JSON.parse(configJson);
          return (
            <InlineInvoiceMessage
              amountUsd={config.amountUsd}
              description={config.description}
              invoiceTitle={config.invoiceTitle}
              clientSecret={config.clientSecret}
              paymentIntentId={config.paymentIntentId}
              targetUid={config.targetUid}
              allowedMethods={config.allowedMethods}
              savedMethods={config.savedMethods}
              stripePublishableKey={config.stripePublishableKey}
              onPaymentComplete={(result) => {
                console.log('[Payment] Success:', result);
              }}
              onPaymentFailed={(error) => {
                console.log('[Payment] Failed:', error);
              }}
            />
          );
        } catch (e) {
          console.error('[SpecialMessageTile] Failed to parse payment config:', e);
        }
      }
    }

    // Handle appointment messages - render InlineAppointmentMessage component
    if (messageType === 'appointment' && html?.includes('inline-appointment-form')) {
      const configMatch = html.match(/data-appointment-config='([^']+)'/);
      if (configMatch) {
        try {
          const configJson = configMatch[1].replace(/&#39;/g, "'");
          const config = JSON.parse(configJson);
          return (
            <InlineAppointmentMessage
              iframeUrl={config.iframeUrl}
              title={config.title}
              instructions={config.instructions}
            />
          );
        } catch (e) {
          console.error('[SpecialMessageTile] Failed to parse appointment config:', e);
        }
      }
    }

    // Default HTML/text rendering
    if (html) {
      // Workflow CTA lifecycle (2026-08-19): once the workflow advanced past
      // this card, in-body CTA buttons render retired. Registry-predicate
      // transform — utility buttons (copy/stop/preview) are never touched.
      const lifecycleHtml = ctaLive
        ? html
        : retireWorkflowCtaButtonsHtml(html, isWorkflowCtaButtonId);
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: ensureLinksOpenInNewTab(normalizeChatHtml(lifecycleHtml)),
          }}
        />
      );
    }
    if (text) {
      // 2026-08-02 (Miles table report): the raw-span fallback bypassed ALL
      // rendering — markdown tables/lists/bold in text-only tiles showed as
      // pipe soup. Route through the same pipeline as plain bubbles.
      //
      // The text is ESCAPED first (review 2026-08-03): the sanitizer UNWRAPS
      // unknown tags rather than escaping them, so angle-bracket tokens that
      // tiles legitimately quote — "<no response>", "<YOUR_API_KEY>" — were
      // parsed as markup and silently deleted from error/info copy.
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: ensureLinksOpenInNewTab(normalizeChatHtml(autoLinkText(escaped))),
          }}
        />
      );
    }
    return null;
  };

  // Determine icon to show
  // Don't show icons for final result displays (research, formatting, video script)
  // These are content outputs, not status messages, so icons are unnecessary visual clutter
  const isVideoScriptResult = sourceTag?.includes('video_script_result');
  const shouldShowIcon = showIcon &&
    messageType !== 'research' &&
    messageType !== 'formatting' &&
    !isVideoScriptResult;
  const iconElement = shouldShowIcon
    ? (icon || MessageTypeIcons[messageType] || MessageTypeIcons.info)
    : null;

  return (
    <>
    <SpecialTileContainer
      $messageType={messageType}
      $priority={priority}
      $animate={animate}
      className={className}
    >
      {/* Header with icon and title */}
      {(title || iconElement) && (
        <SpecialTileHeader>
          {iconElement && (
            <SpecialTileIcon $messageType={messageType}>
              {iconElement}
            </SpecialTileIcon>
          )}
          <div style={{ flex: 1 }}>
            {title && (
              <SpecialTileTitle $messageType={messageType}>
                {title}
              </SpecialTileTitle>
            )}
            {subtitle && (
              <SpecialTileSubtitle>{subtitle}</SpecialTileSubtitle>
            )}
          </div>
          {showTimestamp && timestamp && (
            <SpecialTileTimestamp>
              {formatMessageTime(timestamp)}
            </SpecialTileTimestamp>
          )}
        </SpecialTileHeader>
      )}

      {/* Main content */}
      {(text || html) && (
        <SpecialTileContent>
          {renderContent()}
        </SpecialTileContent>
      )}

      {/* Streaming indicator */}
      {isStreaming && (
        <SpecialTileStreamingIndicator>
          processing...
        </SpecialTileStreamingIndicator>
      )}

      {/* Attachments */}
      {!!(attachments?.length || attachmentsSerialized?.length) && (
        <AttachmentsSection
          attachments={attachments}
          attachmentsSerialized={attachmentsSerialized}
          isUser={false}
          messageId={messageId}
          messageText={text}
        />
      )}

      {/* Workflow action buttons - rendered after attachments.
          Workflow CTA lifecycle (2026-08-19): every button in this container
          is a workflow CTA by construction, so a superseded card retires the
          whole row (selection styling still applies first so the chosen
          button keeps its ✓; the retire transform runs LAST so its grey-out
          styles win). Stop/utility exemptions hold inside the transform. */}
      {workflowActionButtonsHtml && (
        <div
          dangerouslySetInnerHTML={{
            __html: ensureLinksOpenInNewTab(
              ctaLive
                ? applyButtonSelectionState(
                    normalizeLegacyCtaColors(workflowActionButtonsHtml),
                    selectedActionButtonIndex
                  )
                : retireAllButtonsHtml(
                    applyButtonSelectionState(
                      normalizeLegacyCtaColors(workflowActionButtonsHtml),
                      selectedActionButtonIndex
                    )
                  )
            ),
          }}
        />
      )}

      {/* Action buttons */}
      {visibleActionButtons.length > 0 && (
        <SpecialTileActions>
          {visibleActionButtons.map((button, index) => {
            const isSelected = selectedActionButtonIndex === index;
            const isUnselected = selectedActionButtonIndex !== undefined && selectedActionButtonIndex !== index;

            return (
              <SpecialTileActionButton
                key={index}
                $style={button.buttonStyle}
                $loading={loadingButtons.has(index)}
                $selected={isSelected}
                $unselected={isUnselected}
                disabled={button.disabled || loadingButtons.has(index) || isUnselected}
                onClick={() => handleAction(button, index)}
              >
                {button.buttonText}
              </SpecialTileActionButton>
            );
          })}
        </SpecialTileActions>
      )}

      {/* Footer with timestamp, copy, and AI info button - always show for consistency with normal messages */}
      {(() => {
        const usage = tokenUsage || tokenUsageSnapshot;
        // Build usages array from either array props or single prop for multi-model support
        const usagesArray = tokenUsages?.length ? tokenUsages : tokenUsageSnapshots?.length ? tokenUsageSnapshots : undefined;
        const hasTokenData = !!(
          usage?.inputTokens !== undefined ||
          usage?.outputTokens !== undefined ||
          usage?.reasoningTokens !== undefined ||
          usage?.totalTokens !== undefined ||
          usage?.responseTimeMs !== undefined ||
          (usage?.provider && usage.provider !== 'unknown') ||
          (usagesArray && usagesArray.length > 0)
        );

        // The footer always renders (timestamp + copy) for consistency with
        // normal message bubbles. The ATTRIBUTION half — compute time + the ⓘ
        // model-info button — renders once per turn: ONE ⓘ PER TURN (owner
        // directive 2026-09-01, the Connect-Fitbit repro). A card whose usage
        // is an inherited copy of the turn's series defers to the answer
        // bubble that owns it (`suppressAttribution`, decided by
        // turnAttribution.ts); a card-only turn keeps its ⓘ, and the popup
        // shows "no extensive compute was necessary" when no token data exists.

        const processingTime = usage?.responseTimeMs;
        const formattedTime = suppressAttribution ? null : formatProcessingTime(processingTime);

        return (
          <SpecialTileFooter data-attribution-suppressed={suppressAttribution ? '' : undefined}>
            {/* Timestamp */}
            {timestamp && (
              <SpecialTileTimestamp style={{ marginLeft: 0 }}>
                {formatMessageTime(timestamp)}
              </SpecialTileTimestamp>
            )}

            {/* Processing time */}
            {formattedTime && (
              <SpecialTileProcessingTime title="compute time">
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>{formattedTime}</span>
              </SpecialTileProcessingTime>
            )}

            {/* Read aloud — the same control the normal bubble carries, so a
                card is not the one message shape you cannot listen to. */}
            {readAloud && (
              <ReadAloudButton
                type="button"
                data-read-aloud-trigger=""
                $state={readAloud.state}
                aria-label={readAloud.label}
                title={readAloud.label}
                aria-pressed={readAloud.isActive}
                aria-busy={readAloud.state === 'loading'}
                onClick={(e) => {
                  e.stopPropagation();
                  readAloud.onToggle();
                }}
              >
                {readAloud.state === 'idle' ? <SpeakerSvgIcon /> : <StopSvgIcon />}
              </ReadAloudButton>
            )}
            {/* The same player as the transcript rows (2026-09-10): a tile being
                read gets pause / play, start over, how far along it is, and the
                speed slider — on its own row, with room to say what it is doing. */}
            {readAloud && readAloud.isActive && (
              <ReadAloudMiniPlayerLive
                language={readAloud.language}
              />
            )}

            {/* Copy button */}
            <CopyIconButton
              aria-label="copy message"
              title="copy message"
              onClick={(e) => {
                e.stopPropagation();
                if (onCopyMessage) {
                  onCopyMessage();
                } else {
                  // Default copy behavior
                  const content = html ? htmlToPlainText(html) : (text || '');
                  if (content && navigator?.clipboard?.writeText) {
                    navigator.clipboard.writeText(content).catch(() => {});
                  }
                }
              }}
            >
              <svg viewBox="0 0 24 24">
                <rect x="9" y="9" width="11" height="11" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </CopyIconButton>

            {/* AI Info button — omitted when the turn's answer bubble owns the attribution */}
            {!suppressAttribution && (
              <TokenInfoButton
                ref={tokenInfoButtonRef}
                aria-label="view info"
                title="view info"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTokenInfo(!showTokenInfo);
                }}
              >
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </TokenInfoButton>
            )}

            {/* Show test tag badge if message is tagged */}
            {tags?.includes('test example') && (
              <span
                style={{
                  marginLeft: '6px',
                  padding: '2px 6px',
                  fontSize: '0.65rem',
                  fontWeight: 500,
                  color: '#f59e0b',
                  background: 'rgba(245, 158, 11, 0.1)',
                  borderRadius: '4px',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                }}
                title="Tagged as test data"
              >
                test example
              </span>
            )}

            {/* Token Info Popup */}
            {showTokenInfo && !suppressAttribution && (
              <SpecialTileTokenInfoPopup
                tokenUsage={usage}
                tokenUsages={usagesArray}
                anchorRef={tokenInfoButtonRef}
                onClose={() => setShowTokenInfo(false)}
              />
            )}
          </SpecialTileFooter>
        );
      })()}
    </SpecialTileContainer>
    </>
  );
};

// ============================================================================
// Helper function to determine message type from sourceTag
// ============================================================================

/**
 * Maps a sourceTag to the corresponding SpecialMessageType.
 * Used for automatic message type detection from agentic workflows.
 */
export function getMessageTypeFromSourceTag(sourceTag?: string): SpecialMessageType {
  if (!sourceTag) return 'info';

  const tag = sourceTag.toLowerCase();

  if (tag.includes('research')) return 'research';
  if (tag.includes('format')) return 'formatting';
  if (tag.includes('error')) return 'error';
  if (tag.includes('warning')) return 'warning';
  if (tag.includes('success')) return 'success';
  if (tag.includes('alert')) return 'alert';
  if (tag.includes('system')) return 'system';
  if (tag.includes('progress') || tag.includes('streaming')) return 'progress';
  if (tag.includes('notification')) return 'notification';

  return 'info';
}

/**
 * Creates a SpecialMessageActionButton from raw action button data.
 * Useful for converting platform message action buttons to the component format.
 */
export function createActionButton(
  buttonText: string,
  actionType: SpecialMessageActionButton['actionType'],
  actionValue: string,
  buttonStyle: SpecialMessageActionButton['buttonStyle'] = 'secondary',
  onClick?: () => void | Promise<void>
): SpecialMessageActionButton {
  return {
    buttonText,
    actionType,
    actionValue,
    buttonStyle,
    onClick,
  };
}

export default SpecialMessageTile;
